import { type ElementType, useState } from 'react';
import { Link } from 'react-router-dom';

import { Box, IconButton, TableCell, tableCellClasses, TableRow, Tooltip, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';

import { cellValue } from 'commonComponents/MaterialTable';

import { LicenseDetails } from 'api/licenses';
import { CheckCircleIcon, CopyIcon, OpenInNewIcon } from 'icons';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        fontWeight: 'bold',
        borderBottom: '1.8px solid black',
        padding: '20px 0px 0px 5px',
        color: theme.palette.primary,
    },
    [`&.${tableCellClasses.body}`]: {
        padding: '5px',
    },
}));

function LinkTo({
    value,
    linkTo,
    linkType,
    toolTip,
}: {
    value: string;
    linkTo: string;
    linkType: string;
    toolTip: string;
}) {
    const theme = useTheme();

    return (
        <Typography
            style={{ display: 'inherit', width: 'fit-content' }}
            color="primary"
            variant="body2"
            target={linkType}
            component={Link}
            to={linkTo}
        >
            <Tooltip title={toolTip} placement="bottom-start">
                <Box display="flex" alignItems="center" gap={theme.spacing(1)}>
                    {value}
                    {linkType === '_blank' && <OpenInNewIcon style={{ fontSize: 'inherit' }} />}
                </Box>
            </Tooltip>
        </Typography>
    );
}

function CopyToClipboard({ value, text }: { value: string; text: string }) {
    const theme = useTheme();
    const [isCopied, setIsCopied] = useState(false);

    async function copyToClipboard(text: string) {
        return await navigator.clipboard.writeText(text);
    }

    const copy = () => {
        copyToClipboard(text).then(() => {
            setIsCopied(true);
            setTimeout(() => {
                setIsCopied(false);
            }, 3000);
        });
    };

    return (
        <Typography style={{ display: 'inherit', width: 'fit-content' }} variant="body2" component="div">
            <Box display="flex" alignItems="center" gap={theme.spacing(1)}>
                {value}
                {isCopied ? (
                    <Tooltip title="Copied" placement="bottom-start">
                        {/* use an IconButton as well to keep the same size */}
                        <IconButton color="primary" size="small">
                            <CheckCircleIcon style={{ fontSize: 'inherit' }} />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Tooltip title="Copy to Clipboard" placement="bottom-start">
                        <IconButton onClick={copy} color="primary" size="small">
                            <CopyIcon style={{ fontSize: 'inherit' }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Typography>
    );
}

function CellContent({
    rowConfig: { accessor, link, clipboard, showError, component = 'div' },
    details,
}: {
    rowConfig: {
        accessor: string | ((license: LicenseDetails) => string);
        link?: {
            route: (license: LicenseDetails) => string;
            type: string;
            toolTip: string;
        };
        clipboard: (license: LicenseDetails) => string;
        showError?: (license: LicenseDetails) => boolean;
        component?: ElementType;
    };
    details: LicenseDetails;
}) {
    const value = cellValue(details, { accessor: accessor });
    const copyToClipboard = clipboard && clipboard(details);

    if (link) {
        return <LinkTo value={value} linkTo={link.route(details)} linkType={link.type} toolTip={link.toolTip} />;
    }

    if (copyToClipboard) {
        return <CopyToClipboard value={value} text={copyToClipboard} />;
    }

    const isError = showError?.(details) ?? false;

    return (
        <Typography
            component={component}
            variant="body2"
            color={isError ? 'error' : 'textPrimary'}
            sx={{ whiteSpace: 'break-spaces' }}
        >
            {value}
        </Typography>
    );
}

export function LicenseDetailsHeaderRow({ title }) {
    return (
        <TableRow>
            <StyledTableCell style={{ width: '350px' }}>{title}</StyledTableCell>
            <StyledTableCell />
        </TableRow>
    );
}

export function LicenseDetailsRow({ rowConfig, details }) {
    const show = rowConfig.hide == null || !rowConfig.hide(details);
    if (!show) return null;
    const testId = rowConfig.title.toLowerCase().replaceAll(/\W/g, '');

    return (
        <TableRow>
            <TableCell component="th" scope="row" style={{ width: '350px' }}>
                {rowConfig.title}
            </TableCell>

            <TableCell data-testid={`LicenseDetailsRow_${testId}`}>
                <CellContent rowConfig={rowConfig} details={details} />
            </TableCell>
        </TableRow>
    );
}
